import isUndefined from 'lodash/isUndefined';
import isEqual from 'lodash/isEqual';
import size from 'lodash/size';
import React, { Fragment, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import loadable from '@loadable/component';
import classNames from 'classnames';
import IconFilter from '@spothero/icons/slider';
import { Box, Flex, Button, Icon } from '@spothero/ui';
import { Badge } from '@spothero/ui-backlog';
import AmenityUtils from '@spothero/utils/amenity';
import { getSpotsV2 } from 'store/spots/spots-selectors';
import { resetFilters, updateFilters } from 'store/filters/filters-actions';
import { getAmenities, getRatings, getReservationTypes, } from 'store/filters/filters-selectors';
import { VIEW_RATINGS } from 'store/filters/rating-filter-utils';
import trackProductListFiltered from 'segment/events/product-list-filtered';
import FilterItem from './filter-item/FilterItem';
import MobileToggle from 'common/search-filters-controls/mobile-toggle';
import { getParkingType } from 'store/selectors/selectors';
import TotalToggle from './total-toggle/TotalToggle';
const AvailabilityFilters = loadable(() => import('./monthly-options-filter/AvailabilityFilters'));
const FiltersModal = loadable(() => import('./filters-modal/FiltersModal'));
const buttonColor = isCondensed => ({
    backgroundColor: isCondensed ? 'white' : 'transparent',
});
const QuickFilters = ({ className, showReservationTypes, visibleSlugs, extraFilterSlot, isMobile, isCondensed, buttonText = '', isOversizedEnabled = false, hideIcon = false, showModalDefault = false, showTotalToggle = true, }) => {
    const dispatch = useDispatch();
    const amenitiesSelectedOnModalOpen = useRef();
    const [showModal, setShowModal] = useState(showModalDefault);
    const { filteredSpotsNum: numSpots, numActive: numActiveFilters, currentAmenities: activeAmenities, allAmenities: amenities, resTypes: reservationTypes, newActiveReservationType: activeReservationType, allRatings: ratings, currentRatingTypes: activeRatingTypes, } = useSelector((state) => {
        // getReservationTypes is only applicable for monthly
        const resTypes = getReservationTypes(state);
        const allAmenities = getAmenities(state);
        const allRatings = getRatings(state);
        const { filters: { numActive, activeAmenities: currentAmenities, activeReservationType: activeResType, activeRatingTypes: currentRatingTypes, }, } = state;
        let newActiveReservationType = activeResType;
        // check if reservation type is valid
        if (newActiveReservationType !== 'all' &&
            isUndefined(resTypes[newActiveReservationType])) {
            newActiveReservationType = 'all';
        }
        const filteredSpotsNum = getSpotsV2(state).length;
        return {
            numActive,
            allAmenities,
            currentAmenities,
            resTypes,
            newActiveReservationType,
            filteredSpotsNum,
            allRatings,
            currentRatingTypes,
        };
    });
    const storeState = useSelector((state) => state);
    const parkingType = getParkingType(storeState);
    const badgeLabel = numActiveFilters > 0 && activeRatingTypes.length > 0
        ? (numActiveFilters + activeRatingTypes.length).toString()
        : numActiveFilters > 0
            ? numActiveFilters.toString()
            : activeRatingTypes.length > 0
                ? activeRatingTypes.length.toString()
                : null;
    const classes = classNames('QuickFilters', className);
    const trackFilterChange = (newFilters = {}) => {
        trackProductListFiltered({
            state: storeState,
            filters: {
                ...storeState.filters,
                ...newFilters,
            },
        });
    };
    const onAmenityChange = (amenity, isChecked, isQuickFilter = false) => {
        let newActiveAmenities = [...activeAmenities];
        if (isChecked) {
            newActiveAmenities.push(amenity);
        }
        else {
            newActiveAmenities = newActiveAmenities.filter(item => item !== amenity);
        }
        const changedFilters = {
            activeAmenities: newActiveAmenities,
            viaQuickFilter: isQuickFilter,
        };
        const isTrackChange = isQuickFilter && !showModal;
        dispatch(updateFilters(changedFilters));
        if (isTrackChange) {
            trackFilterChange(changedFilters);
        }
    };
    const onRatingChange = (amenity, isChecked) => {
        let newActiveRatings = [...activeRatingTypes];
        if (isChecked) {
            // if view-rating is enabled, we want to add all rating categories
            if (amenity === VIEW_RATINGS) {
                Object.values(ratings).forEach((rating) => {
                    if (rating.active)
                        newActiveRatings.push(rating.slug);
                });
            }
            else {
                newActiveRatings.push(amenity);
            }
        }
        else {
            if (amenity === VIEW_RATINGS) {
                newActiveRatings = [];
            }
            else {
                newActiveRatings = newActiveRatings.filter(item => item !== amenity);
            }
        }
        dispatch(updateFilters({
            activeRatingTypes: newActiveRatings,
            viaQuickFilter: false,
        }));
    };
    const onFilterChange = (amenity, isChecked, isQuickFilter = false) => {
        if (!isUndefined(ratings[amenity]) || amenity === VIEW_RATINGS) {
            onRatingChange(amenity, isChecked);
        }
        else {
            onAmenityChange(amenity, isChecked, isQuickFilter);
        }
    };
    const onClearFilters = () => {
        dispatch(resetFilters());
    };
    const onReservationTypeChange = value => {
        const changedFilters = {
            activeReservationType: value,
        };
        dispatch(updateFilters(changedFilters));
        trackFilterChange(changedFilters);
    };
    const onModalToggle = () => {
        let isTrackChange = false;
        // If modal is being closed and filters have been changed since open (and clear-all hasn't been pressed), track changes
        if (showModal) {
            if (!isEqual(activeAmenities, amenitiesSelectedOnModalOpen.current)) {
                isTrackChange = true;
            }
            amenitiesSelectedOnModalOpen.current = [];
        }
        else {
            amenitiesSelectedOnModalOpen.current = activeAmenities;
        }
        setShowModal(show => !show);
        if (isTrackChange) {
            trackFilterChange();
        }
    };
    /**
     * During the CA fees ticket I attempted to make this a react component ex <FilterButton/>
     * but that resulted in a problem of react lifecycle on mobile safari.
     * Reverting that fixed it (bug for reference https://spothero.atlassian.net/browse/DRAC-1689)
     */
    const filterButton = (<Button className="QuickFilters-filter QuickFilters-FilterButton" data-testid={`QuickFilters-filter${isCondensed ? '-condensed' : ''}`} onClick={onModalToggle} backgroundColor={isCondensed ? 'white' : 'transparent'} paddingY="0" paddingX="3" color="text.primary.light" borderColor="accent" fontWeight="base" fontSize="sm" margin={0} flexGrow={1} flexShrink={1} maxWidth="10rem" sx={{
            '&.QuickFilters-filter': {
                margin: 0,
            },
        }}>
            {badgeLabel ? (<Badge className="QuickFilters-filter-badge">
                    {badgeLabel}
                </Badge>) : hideIcon ? null : (<Icon as={IconFilter}/>)}
            {buttonText || 'Filters'}
        </Button>);
    return (<>
            <Box className={classes}>
                {isMobile && parkingType !== 'monthly' ? (<Flex justifyContent="space-between" flexGrow={1} gap={{ base: '.25rem', tablet: '2rem' }}>
                        {isOversizedEnabled && extraFilterSlot
                ? extraFilterSlot
                : null}
                        {showTotalToggle && <TotalToggle />}
                        {filterButton}
                    </Flex>) : (<Flex className={classNames({
                'QuickFilters-group': Boolean(extraFilterSlot),
            })} sx={{
                '.FilterItem-label': buttonColor(isCondensed),
                '.QuickFilters-filter': buttonColor(isCondensed),
                // To clear the styling conflict caused by the above line for all filters
                // we manually set the color for the Total Price button
                '.QuickFilters-TotalToggle--active': {
                    backgroundColor: 'primary.default',
                    color: 'white',
                },
                '.QuickFilters-TotalToggle--inactive': {
                    backgroundColor: 'transparent',
                    color: 'black',
                },
                '.FormElement-item': buttonColor(isCondensed),
            }} justifyContent={parkingType === 'airport' && !showTotalToggle
                ? 'end'
                : 'space-between'} flexGrow={1} gap={{ base: '.25rem', tablet: '2rem' }}>
                        {!showReservationTypes &&
                Boolean(size(amenities)) &&
                visibleSlugs.length > 0 && (<Box className="QuickFilters-items" flexGrow="1">
                                    {visibleSlugs.map(slug => {
                    const amenity = amenities[slug];
                    if (!amenity) {
                        return null;
                    }
                    // if no filters are active and this amenity doesn't apply to the current search, hide it
                    // so that it does not show up as a disabled amenity when it really has no business being there
                    const isHidden = numActiveFilters === 0 &&
                        amenity.count === 0;
                    const isTouchless = slug === 'touchless';
                    const touchlessId = 'QuickFilter-touchless';
                    if (isHidden) {
                        return null;
                    }
                    return (<Fragment key={amenity.slug}>
                                                <FilterItem {...(isTouchless && {
                        id: touchlessId,
                    })} slug={amenity.slug} label={amenity.name} defaultChecked={activeAmenities.includes(amenity.slug)} disabled={
                        // Backlog elements weren't changed to isDisabled
                        amenity.count === 0} isQuickFilter onChange={onFilterChange}/>
                                            </Fragment>);
                })}
                                </Box>)}
                        {showReservationTypes && (<AvailabilityFilters activeReservationType={activeReservationType} reservationTypes={reservationTypes} onChange={onReservationTypeChange}/>)}
                        {extraFilterSlot && !isMobile ? extraFilterSlot : null}
                        {showTotalToggle && <TotalToggle />}
                        {filterButton}
                    </Flex>)}
                <FiltersModal numSpots={numSpots} amenities={amenities} activeAmenities={activeAmenities} numActiveFilters={numActiveFilters} onChange={onFilterChange} onClear={onClearFilters} onHidden={onModalToggle} isOpen={showModal} allRatings={ratings} activeRatingTypes={activeRatingTypes}/>
            </Box>
            {isMobile && parkingType === 'monthly' ? (<Box className={classes} gap="3">
                    <MobileToggle />
                </Box>) : null}
        </>);
};
QuickFilters.propTypes = {
    className: PropTypes.string,
    showReservationTypes: PropTypes.bool,
    isMobile: PropTypes.bool,
    visibleSlugs: PropTypes.arrayOf(PropTypes.oneOf(Object.keys(AmenityUtils.ICONS))),
    extraFilterSlot: PropTypes.object,
    isCondensed: PropTypes.bool,
    buttonText: PropTypes.string,
    isOversizedEnabled: PropTypes.bool,
};
QuickFilters.defaultProps = {
    visibleSlugs: [],
};
export default QuickFilters;
